"use client";

import { EyeIcon, EyeSlashIcon } from "@/app/assets/icons";
import { useAutoTranslate } from "@/lib/i18n/useAutoTranslate";
import React, {
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  useRef,
  useState,
} from "react";

type InputFieldProps = {
  label?: string;
  name: string;
  error?: string;
  type?: string;
  icon?: React.ReactNode;
  touched?: boolean;
  asTextarea?: boolean;
  inputStyle?: string;
  autoComplete?: string;
} & InputHTMLAttributes<HTMLInputElement> &
  TextareaHTMLAttributes<HTMLTextAreaElement>;

export default React.memo(function InputField({
  label,
  name,
  icon,
  type = "text",
  error,
  touched,
  asTextarea = false,
  inputStyle,
  autoComplete,
  ...rest
}: InputFieldProps) {
  const tr = useAutoTranslate();
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isPassword = type === "password";
  const isFile = type === "file";
  const inputType = isPassword ? (showPassword ? "text" : "password") : type;
  const showError = touched && error;
  const disabled = rest.disabled;

  const handleDivClick = (e: React.MouseEvent) => {
    // Don't trigger if the click is directly on the input element
    if (e.target === fileInputRef.current) {
      return;
    }

    if (isFile && fileInputRef.current && !disabled) {
      fileInputRef.current.click();
    }
  };

  return (
    <div>
      {label && (
        <label
          htmlFor={name}
          className={`block text-sm font-medium mb-1 ${
            disabled ? "text-gray-400" : "text-input-label"
          }`}
        >
          {tr(label)}
        </label>
      )}

      <div
        className={`w-full rounded-xl border overflow-hidden flex justify-between items-center gap-0 sm:text-sm
          ${showError ? "border-red-500" : "border-border"}
          ${disabled ? "bg-gray-100 cursor-not-allowed" : "bg-input-bg"}
          ${asTextarea ? "items-start" : ""}
          ${isFile && !disabled ? "cursor-pointer" : ""}
        `}
        onClick={handleDivClick}
      >
        {asTextarea ? (
          <textarea
            id={name}
            name={name}
            autoComplete={autoComplete}
            className={`outline-none border-none w-full px-3 py-2 resize-none h-24
              ${
                disabled
                  ? "bg-input-bg text-gray-500 placeholder-gray-400 cursor-not-allowed"
                  : "bg-input-bg"
              }`}
            {...rest}
            placeholder={tr(rest.placeholder as string)}
          />
        ) : (
          <input
            ref={isFile ? fileInputRef : undefined}
            id={name}
            name={name}
            type={inputType}
            autoComplete={autoComplete}
            className={`outline-none border-none w-full px-3 py-2.5
              ${inputStyle}
              ${
                disabled
                  ? "bg-input-bg text-gray-500 placeholder-gray-400 cursor-not-allowed"
                  : `bg-input-bg ${type === "file" ? "cursor-pointer" : ""}`
              }`}
            {...rest}
            placeholder={tr(rest.placeholder as string)}
          />
        )}

        {!asTextarea && (isPassword || icon) && (
          <div className="pr-3">
            {isPassword ? (
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="w-5 h-5"
                tabIndex={-1}
                disabled={disabled}
              >
                <span
                  className={disabled ? "cursor-not-allowed" : "cursor-pointer"}
                >
                  {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </span>
              </button>
            ) : (
              <div
                className={`${isFile && !disabled ? "cursor-pointer" : ""}`}
                onClick={
                  isFile
                    ? (e) => {
                        e.stopPropagation();
                        if (fileInputRef.current && !disabled) {
                          fileInputRef.current.click();
                        }
                      }
                    : undefined
                }
              >
                {icon}
              </div>
            )}
          </div>
        )}
      </div>

      {showError && <p className="mt-1 text-xs text-red-500">{tr(error)}</p>}
    </div>
  );
});
